import { useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  isPersistableModelProvider,
  matchModelProviderByPrefix,
  ModelProviderSchema,
} from '@reflex/shared';
import type { ModelProvider, ModelProviderSecretType } from '@reflex/shared';
import { getAgentModelSupport } from './generated-internal/config';
import { queryKeys } from './query-keys';
import { AuthContext, useOrgKey } from './PluginClientContext';

/**
 * The slice of the org's model-support matrix this module reads. Structural
 * (rather than importing the generated response type) so both the generated
 * client shape and hand-built test fixtures satisfy it.
 */
export interface AgentModelSupportMatrix {
  agents?: Record<string, AgentModelSupportMatrixEntry | undefined>;
  /** Agent type a surface launches with when it pins none of its own. */
  defaultAgentType?: string;
}

export interface AgentModelSupportMatrixEntry {
  status: string;
  providers?: readonly {
    id: string;
    keyTypes?: readonly string[];
    modelProvider?: string;
  }[];
  defaultProvider?: string;
  discoveredModels?: readonly { id: string; providerId: string }[];
}

/**
 * Which model-provider keys a surface should offer for an agent type.
 * `keyTypes` is empty for a free-tier provider that needs no key at all —
 * callers hide the picker entirely in that case.
 */
export interface AgentProviderKeyTarget {
  provider: ModelProvider;
  keyTypes: readonly ModelProviderSecretType[];
}

export interface LoadedAgentProviderKeyTarget extends AgentProviderKeyTarget {
  /** Whether the model-support matrix has supplied a concrete answer. */
  matrixLoaded: boolean;
  /** Whether loading the matrix failed, so callers should use the fallback. */
  matrixError: boolean;
}

/** What a provider offers when the matrix doesn't say (back-compat). */
const DEFAULT_KEY_TYPES: readonly ModelProviderSecretType[] = ['apiKey'];

/** Claude Code is the default agent, so an unresolvable agent lands here. */
const FALLBACK_PROVIDER: ModelProvider = 'anthropic';

function keyTarget(
  provider: ModelProvider,
  keyTypes: readonly ModelProviderSecretType[],
): AgentProviderKeyTarget {
  // Runloop-managed models use the deployment's service secret rather than a
  // stored model-provider key. Keeping that invariant here makes every
  // provider-key surface agree, including while the catalog is unavailable.
  return {
    provider,
    keyTypes: isPersistableModelProvider(provider) ? keyTypes : [],
  };
}

function toModelProvider(value: string | undefined): ModelProvider | undefined {
  const parsed = ModelProviderSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

function toKeyTypes(values: readonly string[] | undefined): readonly ModelProviderSecretType[] {
  if (!values) return DEFAULT_KEY_TYPES;
  const known = values.filter(
    (v): v is ModelProviderSecretType => v === 'apiKey' || v === 'subscription',
  );
  // An empty list from the server means "needs no key"; a list of only types
  // this client predates does not — don't hide the picker over a newer server.
  if (known.length === 0 && values.length > 0) return DEFAULT_KEY_TYPES;
  return known;
}

/**
 * Resolve which provider's keys an agent node should offer, mirroring the
 * server's own resolution in the flow editor's `list_provider_secrets` tool.
 *
 * The agent type — not the model string — is what decides this: a Cursor
 * node routing to `auto` (or to a model named after another vendor, which
 * Cursor proxies) still needs a Cursor key. Resolution order:
 *
 * 1. An explicit provider the user picked in the settings chip (even before a
 *    model is chosen — switching to Fireworks must gate on a Fireworks key).
 * 2. The provider the discovery catalog attributes this model to.
 * 3. A model-id prefix match, but only when it names one of the agent's own
 *    providers (covers free-text models missing from the catalog).
 * 4. The agent's default provider, else its first provider.
 * 5. With no matrix entry at all (catalog still loading, or an agent that
 *    isn't multi-model): a bare prefix match on the model, then Anthropic.
 *
 * A provider `id` that isn't itself a canonical {@link ModelProvider} (e.g.
 * the OpenCode free tier) maps through its `modelProvider` field.
 */
export function resolveAgentProviderKeyTarget(
  entry: AgentModelSupportMatrixEntry | null | undefined,
  model: string | undefined,
  selectedProviderId?: string | null,
): AgentProviderKeyTarget {
  const guessed = model ? matchModelProviderByPrefix(model) : undefined;
  const providers = entry?.status === 'available' ? (entry.providers ?? []) : [];
  if (providers.length === 0) {
    return keyTarget(guessed ?? FALLBACK_PROVIDER, DEFAULT_KEY_TYPES);
  }

  const catalogProviderId = model
    ? entry?.discoveredModels?.find((m) => m.id === model)?.providerId
    : undefined;
  const selected =
    (selectedProviderId ? providers.find((p) => p.id === selectedProviderId) : undefined) ??
    providers.find((p) => p.id === catalogProviderId) ??
    (guessed ? providers.find((p) => (p.modelProvider ?? p.id) === guessed) : undefined) ??
    providers.find((p) => p.id === entry?.defaultProvider) ??
    providers[0];

  const provider =
    toModelProvider(selected.modelProvider) ??
    toModelProvider(selected.id) ??
    guessed ??
    FALLBACK_PROVIDER;
  return keyTarget(provider, toKeyTypes(selected.keyTypes));
}

/**
 * Reads the org's agent/model/provider matrix. Shares the host app's
 * `useAgentModelSupport` cache entry through
 * {@link queryKeys.agentModelSupport}, so a plugin surface reuses an
 * already-loaded response rather than refetching.
 *
 * Only fetches for a signed-in reader. The host reads this key through the
 * docs live-block adapter, which serves a fixture (and never touches the
 * network) for anonymous readers and inside the guided-demo simulation
 * shell; an unconditional fetch here would populate the shared cache entry
 * with live workspace data on exactly those surfaces.
 */
export function useAgentModelSupportMatrix() {
  const orgKey = useOrgKey();
  const auth = useContext(AuthContext);
  return useQuery({
    queryKey: queryKeys.agentModelSupport(orgKey),
    queryFn: () => getAgentModelSupport() as Promise<AgentModelSupportMatrix>,
    enabled: auth?.authenticated === true,
    staleTime: 60_000,
  });
}

/**
 * Which provider's keys to offer for an agent type + model. See
 * {@link resolveAgentProviderKeyTarget} for the resolution order. While the
 * matrix loads this returns a model-heuristic fallback with `matrixLoaded`
 * false; surfaces that render an error state should wait for the matrix rather
 * than briefly displaying a misleading warning. If the request fails,
 * `matrixError` is true and callers should use that conservative fallback. A
 * caller that pins no agent type (a surface inheriting the org default)
 * resolves against the matrix's `defaultAgentType`, which is what such a
 * launch actually runs with.
 *
 * Pass `selectedProviderId` when the surface lets the user pick a provider
 * independently of the model (persona editor, launch chip) so a deliberate
 * provider switch is what the key gate evaluates — not the default.
 */
export function useAgentProviderKeyTarget(
  agentType: string | undefined,
  model: string | undefined,
  selectedProviderId?: string | null,
): LoadedAgentProviderKeyTarget {
  const { data, isError: matrixError } = useAgentModelSupportMatrix();
  return useMemo(() => {
    const resolvedType = agentType ?? data?.defaultAgentType;
    return {
      ...resolveAgentProviderKeyTarget(
        resolvedType ? data?.agents?.[resolvedType] : null,
        model,
        selectedProviderId,
      ),
      matrixLoaded: data !== undefined,
      matrixError,
    };
  }, [data, matrixError, agentType, model, selectedProviderId]);
}
